import Phaser from 'phaser';
import { getAuth, updateEmail } from '../../store/auth.js';
import { Button } from './Button.js';
import { TextInputOverlay } from './TextInputOverlay.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../PhaserGame.js';

const BACKDROP_ALPHA = 0.78;
const BACKDROP_DEPTH = 1000;
const PANEL_DEPTH = 1001;
const PANEL_WIDTH = 560;
const PANEL_HEIGHT = 540;
const PANEL_RADIUS = 12;

type State = 'ready' | 'working' | 'success' | 'error';

export interface ChangeEmailModalOptions {
  readonly onCancel: () => void;
  /** Called after the change request is accepted (Supabase sent the
   *  confirmation email to the new address). The modal stays on
   *  screen showing the success state — caller may show a toast or
   *  just leave it; closing happens on the modal's own button. */
  readonly onSuccess: () => void;
}

/**
 * Email-change flow. Submitting hits `supabase.auth.updateUser({
 * email })` which triggers two Supabase emails: a "Confirm new
 * email" link to the NEW address, and (after they click it) a
 * "Your email was changed" notification to the OLD address. Both
 * templates are already branded; this modal only handles the
 * client-side request + status display.
 */
export class ChangeEmailModal {
  private readonly scene: Phaser.Scene;
  private readonly opts: ChangeEmailModalOptions;

  private readonly objects: Phaser.GameObjects.GameObject[] = [];
  private bodyObjects: Phaser.GameObjects.GameObject[] = [];
  private buttons: Button[] = [];
  private input: TextInputOverlay | null = null;

  private currentState: State = 'ready';
  private typedValue = '';
  private errorMsg = '';
  private destroyed = false;

  constructor(scene: Phaser.Scene, opts: ChangeEmailModalOptions) {
    this.scene = scene;
    this.opts = opts;

    this.drawBackdrop();
    this.drawPanel();
    this.renderBody();

    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.destroy();
    });
  }

  // --- Chrome ---

  private drawBackdrop(): void {
    const bg = this.scene.add
      .rectangle(
        DESIGN_WIDTH / 2,
        DESIGN_HEIGHT / 2,
        DESIGN_WIDTH,
        DESIGN_HEIGHT,
        0x000000,
        BACKDROP_ALPHA
      )
      .setDepth(BACKDROP_DEPTH)
      .setInteractive();
    // No backdrop-tap cancel. The text input is an HTML overlay
    // positioned over this rectangle — taps that land outside the
    // input's hit area (or arrive a frame before focus claims them)
    // would otherwise close the modal mid-type. The Cancel button is
    // the only dismiss path. setInteractive() stays so the backdrop
    // still swallows taps so they don't leak through to the
    // SettingsScene buttons underneath.
    this.objects.push(bg);
  }

  private drawPanel(): void {
    const left = DESIGN_WIDTH / 2 - PANEL_WIDTH / 2;
    const top = DESIGN_HEIGHT / 2 - PANEL_HEIGHT / 2;

    const panel = this.scene.add.graphics().setDepth(PANEL_DEPTH);
    panel.fillStyle(COLORS.bgDeepInt, 1);
    panel.fillRoundedRect(left, top, PANEL_WIDTH, PANEL_HEIGHT, PANEL_RADIUS);
    panel.lineStyle(2, COLORS.accentCyanInt, 0.95);
    panel.strokeRoundedRect(left, top, PANEL_WIDTH, PANEL_HEIGHT, PANEL_RADIUS);
    this.objects.push(panel);
  }

  // --- State-driven body ---

  private renderBody(): void {
    for (const o of this.bodyObjects) o.destroy();
    this.bodyObjects = [];
    this.input?.destroy();
    this.input = null;
    for (const b of this.buttons) b.destroy();
    this.buttons = [];

    switch (this.currentState) {
      case 'ready':
        this.renderReady();
        break;
      case 'working':
        this.renderWorking();
        break;
      case 'success':
        this.renderSuccess();
        break;
      case 'error':
        this.renderError();
        break;
    }
  }

  private renderReady(): void {
    const cx = DESIGN_WIDTH / 2;
    const top = DESIGN_HEIGHT / 2 - PANEL_HEIGHT / 2;

    this.addTitle('Change email', top + 36, COLORS.accentCyanHex);

    const auth = getAuth();
    const currentEmail = auth.user?.email ?? '(unknown)';
    this.addBody(
      `Current: ${currentEmail}`,
      top + 88,
      COLORS.textSecondary
    );

    this.addBody(
      'Enter your new email address. We’ll send a confirmation link to that address; the change takes effect when you click it.',
      top + 148,
      COLORS.textPrimary,
      'left'
    );

    this.input = new TextInputOverlay({
      scene: this.scene,
      x: cx,
      y: top + 280,
      width: 420,
      height: 64,
      type: 'email',
      placeholder: 'new@email.com',
      autocomplete: 'email',
      fontSizeDesign: 22,
      onChange: (v) => {
        this.typedValue = v;
        this.refreshSubmitEnabled();
      },
      onSubmit: () => {
        if (this.canSubmit()) void this.handleSubmit();
      },
    });

    this.addCancelButton(top + PANEL_HEIGHT - 56, cx - 130);
    this.addSubmitButton(top + PANEL_HEIGHT - 56, cx + 130);
    this.refreshSubmitEnabled();
  }

  private renderWorking(): void {
    const top = DESIGN_HEIGHT / 2 - PANEL_HEIGHT / 2;
    this.addTitle('Sending confirmation…', top + 36, COLORS.accentCyanHex);
    this.addBody(
      'Asking the server to send the confirmation link to your new address.',
      top + 200,
      COLORS.textMuted
    );
    // No buttons — short-lived state.
  }

  private renderSuccess(): void {
    const top = DESIGN_HEIGHT / 2 - PANEL_HEIGHT / 2;
    const cx = DESIGN_WIDTH / 2;

    this.addTitle('Check your new email', top + 36, COLORS.accentCyanHex);
    this.addBody(
      `We sent a confirmation link to ${this.typedValue}.`,
      top + 120,
      COLORS.textPrimary
    );
    this.addBody(
      'Click the link to finish the change. Your current address will keep working until you confirm.',
      top + 200,
      COLORS.textSecondary,
      'left'
    );

    const close = new Button(this.scene, {
      x: cx,
      y: top + PANEL_HEIGHT - 56,
      width: 220,
      height: 64,
      text: 'Done',
      style: 'primary',
      onTap: () => {
        this.opts.onSuccess();
        this.destroy();
      },
    });
    close.setDepth(PANEL_DEPTH + 2);
    this.buttons.push(close);
  }

  private renderError(): void {
    const top = DESIGN_HEIGHT / 2 - PANEL_HEIGHT / 2;
    const cx = DESIGN_WIDTH / 2;

    this.addTitle("Couldn't change email", top + 36, COLORS.accentRedHex);
    this.addBody(this.errorMsg, top + 140, COLORS.textPrimary, 'left');

    this.addCancelButton(top + PANEL_HEIGHT - 56, cx - 130);
    const retry = new Button(this.scene, {
      x: cx + 130,
      y: top + PANEL_HEIGHT - 56,
      width: 220,
      height: 64,
      text: 'Try again',
      style: 'primary',
      onTap: () => {
        this.currentState = 'ready';
        this.renderBody();
      },
    });
    retry.setDepth(PANEL_DEPTH + 2);
    this.buttons.push(retry);
  }

  // --- Logic ---

  private canSubmit(): boolean {
    const v = this.typedValue.trim();
    return (
      this.currentState === 'ready' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
    );
  }

  private refreshSubmitEnabled(): void {
    const submit = this.buttons[this.buttons.length - 1];
    if (!submit) return;
    submit.setEnabled(this.canSubmit());
  }

  private async handleSubmit(): Promise<void> {
    if (!this.canSubmit()) return;
    const newEmail = this.typedValue.trim();
    this.currentState = 'working';
    this.renderBody();

    try {
      const result = await updateEmail(newEmail);
      if (this.destroyed) return;
      if (!result.ok) {
        this.errorMsg = result.error;
        this.currentState = 'error';
        this.renderBody();
        return;
      }
      // Supabase accepted the request and queued a confirmation
      // email to the NEW address. The auth.users row's email
      // doesn't actually change until the user clicks the link.
      this.currentState = 'success';
      this.renderBody();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.errorMsg = `Network error: ${msg}`;
      this.currentState = 'error';
      this.renderBody();
    }
  }

  private handleCancel(): void {
    if (this.currentState === 'working') return;
    this.opts.onCancel();
    this.destroy();
  }

  // --- Body helpers ---

  private addTitle(text: string, y: number, color: string): void {
    const t = this.scene.add
      .text(DESIGN_WIDTH / 2, y, text, {
        fontFamily: FONT_HEADER,
        fontSize: '26px',
        color,
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5, 0)
      .setDepth(PANEL_DEPTH + 1);
    this.bodyObjects.push(t);
  }

  private addBody(
    text: string,
    y: number,
    color: string,
    align: 'center' | 'left' = 'center'
  ): void {
    const t = this.scene.add
      .text(DESIGN_WIDTH / 2, y, text, {
        fontFamily: FONT_MONO,
        fontSize: '15px',
        color,
        align,
        wordWrap: { width: PANEL_WIDTH - 80 },
      })
      .setLineSpacing(4)
      .setOrigin(0.5, 0)
      .setDepth(PANEL_DEPTH + 1);
    this.bodyObjects.push(t);
  }

  private addCancelButton(y: number, x: number): void {
    const btn = new Button(this.scene, {
      x,
      y,
      width: 220,
      height: 64,
      text: 'Cancel',
      onTap: () => this.handleCancel(),
    });
    btn.setDepth(PANEL_DEPTH + 2);
    this.buttons.push(btn);
  }

  private addSubmitButton(y: number, x: number): void {
    const btn = new Button(this.scene, {
      x,
      y,
      width: 220,
      height: 64,
      text: 'Send link',
      style: 'primary',
      onTap: () => {
        if (this.canSubmit()) void this.handleSubmit();
      },
    });
    btn.setDepth(PANEL_DEPTH + 2);
    this.buttons.push(btn);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const o of this.objects) o.destroy();
    this.objects.length = 0;
    for (const o of this.bodyObjects) o.destroy();
    this.bodyObjects = [];
    this.input?.destroy();
    this.input = null;
    for (const b of this.buttons) b.destroy();
    this.buttons = [];
  }
}
